import { reactive, computed, effectScope, getCurrentInstance, onMounted } from "vue";
import { useEmployees } from "@/composables/employees/useEmployees";
import { usePositions } from "@/composables/positions/usePositions";
import { useFilters } from "@/composables/filters/useFilters";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { useEmployeeStore } from "@/store/pinia/employee";
import {
  recalculateEmployeeReadiness,
  getChatterList,
  userResendInvite,
  cancelInviteUser,
  getListofEmployeesV2,
  getListofUser,
  startViewAs,
  startPreview,
  exchangeViewAs,
  exchangePreview,
} from "@/api";
import { updateUserAvatarInStorage } from "@components/helper/localStorageHelper";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import { matchIndicatorKey } from "@components/HeaderDashboardCards/matchIndicatorKey";
import { requestAccessPermissionSync } from "@/utils/refreshAccessPermission";
import { mapCheckEmailApiToLegacyModalState } from "@components/GiveAccessToEmployeeWizard/giveAccessToEmployeeEmailValidation.js";
import {
  employeeRadioFiltersToApi,
  getEmployeeRadioFilterLabel,
  getEmployeeRadioFilterValueFromLabel,
} from "@components/FilterEmployeeModal/employeeFilterPayload";
import {
  getPositionId,
  getListOfPositionsForAssignment,
  getListOfPositionsForPrepareAssignment,
} from "@/api";
import {
  LD_FLAG_IDEA_MDP_503_VIEW_AS,
  LD_FLAG_IDEA_MDP_450,
  LD_FLAG_IDEA_MDP_647,
} from "@components/constants/launchDarklyFlagKeys";
import {
  fetchEmployeeVacancy,
  getAssignmentRejectionMessage,
  loadEqualVacancyPositions,
} from "@components/utils/primaryAssignmentPositions";

import { LD_FLAG_EMPLOYEE_MULTI_FIELD_SEARCH } from "@components/constants/launchDarklyFlagKeys";
import {
  MODE_PREVIEW,
  MODE_VIEW_AS,
} from "@components/utils/impersonationSession";
import {
  bindCanStartViewAsAccessListener,
  currentUserCanStartViewAs,
  getViewAsTargetStatus,
  refreshCanStartViewAs,
} from "@components/utils/viewAsEligibility";
import {
  buildViewAsConfirmMessage,
  startAndEnterViewAsOrPreview,
} from "@components/utils/startViewAsSession";
import {
  setViewAsFeatureFlag,
  viewAsLdEnabled,
} from "@components/utils/viewAsFeatureFlag";
import {
  EMPLOYEE_SEARCH_PLACEHOLDER_READINESS_ON,
  getEmployeeMultiFieldSearchFields,
  getEmployeeSearchPlaceholder,
  stripPreparesEmployeeFilterValues,
} from "@components/utils/preparingForOrganizationUi";
import { isProductReadinessEnabled } from "@/utils/accountProductFlags";
import { getOrganizationEmployeeDefaultQueryTab } from "@/utils/organizationEmployeeDetailTabs";
import { normalizeAdminPermission } from "@/utils/defaultLoggedInRedirect";
import {
  isValidEmployeeId,
  parseStoredAuthContext,
  resolveAuthEmployeeId,
} from "@components/utils/authEmployeeContext";

const DEFAULT_EMPLOYEES_ORGANIZATION_FILTERS = getDefaultFiltersForKey(
  "employeesOrganizationTable"
);

/**
 * Query keys used for the employee list search. The simple search box sets `name`; the
 * multi-field box (ER-10611) sets `name`, `primary_position_name` and
 * `preparing_position_name` (backend OR-search in the employee v2 endpoint). Clearing only
 * `name` would otherwise leave the position filters applied, causing a stale API request.
 */
export const EMPLOYEE_LIST_SEARCH_PARAM_KEYS = [
  "name",
  "primary_position_name",
  "preparing_position_name",
];

/**
 * Placeholder / help text for the employees list search box (ER-11430). Describes the
 * three fields the employee search covers (backend OR-search on employee name, primary
 * position and preparing-for position) so users know what they can type up front.
 */
export const EMPLOYEE_SEARCH_PLACEHOLDER = EMPLOYEE_SEARCH_PLACEHOLDER_READINESS_ON;

/**
 * ER-11658: single shared mapper for the Archived filter's two stable values
 * (`show_archived` / `no_archived`). `syncEmployeeApiFiltersFromState` needs to know whether
 * the group is applied and, if so, which direction — this keeps it from re-accepting the
 * retired label strings ("Show" / "Do not Show").
 *
 * @param {Array} values - current `state.filters.values`
 * @returns {boolean|undefined} true (show archived), false (no archived), undefined (not applied)
 */
export const resolveArchivedFilterValue = (values) => {
  const list = Array.isArray(values) ? values : [];
  if (list.includes("show_archived")) return true;
  if (list.includes("no_archived")) return false;
  return undefined;
};

/**
 * ER-11654: filter combination each header-dashboard tile applies (stable values).
 * Tiles 2, 4 and 5 are compound — they set Status plus one radio group.
 */
export const EMPLOYEE_INDICATOR_FILTERS = Object.freeze({
  active: Object.freeze(["active"]),
  "no-access": Object.freeze(["active", "no_access"]),
  draft: Object.freeze(["draft"]),
  "no-primary-position": Object.freeze(["active", "does_not_occupy"]),
  "no-prepare-for": Object.freeze(["active", "does_not_prepare"]),
});

/** ER-12619: filter values that never render as chips, so they must not break the tile match. */
const EMPLOYEE_INDICATOR_NEUTRAL_VALUES = Object.freeze(["no_filter", "no_archived"]);

let instance = null;
let applyingFiltersKey = null; // Filter set currently being applied; identical repeat calls are skipped

export function useEmployeesOrganizationTableViewState() {
  if (instance) return instance;
  const employees = useEmployees();
  const employeeStore = useEmployeeStore();
  const positions = usePositions();
  const { highlight } = useHighlightRow();
  const filter = useFilters();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  const state = reactive({
    filters: {
      values: [...DEFAULT_EMPLOYEES_ORGANIZATION_FILTERS],
      searchText: "",
      searchGhostText: getEmployeeSearchPlaceholder(),
      showFilterDialog: false,
      key: "employeesOrganizationTable",
      clearSelection: "",
      // Multi-field search (ER-10611) — array of { fieldKey, fieldLabel, value }.
      multipleFieldSearchValues: [],
      // Department filter (MDP-440) — object { id, name, ... } + scope "direct" | "subtree".
      department: null,
      departmentScope: "subtree",
      statusOptions: ["draft", "active", "terminated"],
      // ER-11658: every group carries a stable `key` alongside its display `label`, so
      // anything that needs to identify a group (e.g. the `hide_archived_filter_employees`
      // kill switch) matches on the key, not on copy that is free to change.
      filterGroups: [
        {
          key: "access",
          label: "Access",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "No Access", value: "no_access" },
            { label: "Has Access", value: "has_access" },
          ],
        },
        {
          key: "assignments",
          label: "Assignments",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Has Assignments", value: "has_assignment" },
            { label: "No Assignments", value: "no_assignment" },
          ],
        },
        {
          key: "archived",
          label: "Archived",
          items: [
            { label: "Do Not Show", value: "no_archived" },
            { label: "Show", value: "show_archived" },
          ],
        },
      ],
    },
    // Table config surfaced to UniversalTable (ER-10611): multi-field search dropdown.
    config: {
      // ER-11430: tell users which fields the search covers (name / primary / preparing).
      searchInstructions: getEmployeeSearchPlaceholder(),
      showSearchPositionByMultipleFieldBox: true,
      searchPositionByMultipleFields: getEmployeeMultiFieldSearchFields([
        { key: "name", label: "Employee name" },
        { key: "primary_position_name", label: "Primary position" },
        { key: "preparing_position_name", label: "Preparing for" },
      ]),
    },
    archiveAction: null,
    loading: {
      table: false,
      assignmentWizard: false,
      relieveAssignment: false,
      employeeAssignment: false,
    },
    assignmentWizard: {
      show: false,
      selectedPosition: null,
      isAssigning: false,
      rejectionMessage: "",
      effectiveFrom: null,
      effectiveTo: null,
    },
    preparePosition: {
      show: false,
      selectedPosition: null,
      isAssigning: false,
    },
    relieveAssignment: {
      show: false,
      currentPosition: "",
      confirmationPending: false,
      type: "",
    },
    activeEmployeeModal: {
      show: false,
      message: "",
    },
    terminateEmployeeModal: {
      show: false,
      message: "",
    },
    suspendEmployeeAccessModal: {
      show: false,
      message: "",
    },
    giveBackEmployeeAccessModal: {
      show: false,
      message: "",
    },
    resendInviteModal: {
      show: false,
    },
    cancelInviteModal: {
      show: false,
    },
    viewAsConfirmModal: {
      show: false,
      message: "",
      mode: null,
      targetStatus: "",
      employee: null,
    },
    viewAsFeatureEnabled: false,
    canStartViewAs: false,
    addEmployeeModal: {
      show: false,
      message: "",
    },
    changePictureModal: {
      show: false,
      avatarValue: null,
      croppedImage: null,
    },
    giveAccessModal: {
      show: false,
      dataType: "step1",
      existingUser: null,
      showEmailLoadingIcon: false,
      access_level: null,
    },
    giveBackAccessModal: {
      show: false,
    },
    reactivateEmployeeModal: {
      show: false,
      message: "",
    },
    deleteEmployeeModal: {
      show: false,
      message: "",
    },
    archiveEmployeeModal: {
      show: false,
      message: "",
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalEmployeeChatter: 0,
      page: 1,
    },
    table: {
      selectedEmployees: [],
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    currentRow: null,
    currentEmployee: null,
    assignmentPosition: {
      show: false,
      currentPosition: "",
    },
    paginationRoleModel: {
      page: 1,
      total: 0,
      perPage: 10,
    },
    searchMode: false,
    rolesList: [],
    responsibityList: [],
    assignmentItems: [],
    sanitizedItems: [],
    showRoleToPosition: false,
    attachmentId: null,
    unmatchedResponsibilities: [],
    showAssignmentCreationFlag: false,
    primaryPositions: {
      availablePositions: [],
      isLoadingMore: false,
      currentPage: 1,
      totalCount: 0,
      hasMoreData: true,
      currentSearchTerm: "",
      searchNoResults: false,
      equalVacancy: false,
      employeeVacancy: null,
      serverOffset: 0,
    },
    preparePositions: {
      availablePositions: [],
      isLoadingMore: false,
      currentPage: 1,
      totalCount: 0,
      hasMoreData: true,
      currentSearchTerm: "",
      searchNoResults: false,
    },
    featureFlags: {
      employeeAvailabilitySelector: true,
      avatarOpensChangePicture: false,
    },
  });

  const hasSelectedEmployees = computed(
    () => state.table.selectedEmployees.length > 0
  );

  const loadFiltersOnReload = () => {
    const savedFilters = filter.getFilters(state.filters.key);
    const normalized = Array.isArray(savedFilters) ? savedFilters : [];
    if (normalized.length > 0) {
      state.filters.values = normalized;
    } else {
      // If no saved filters, use default filters
      state.filters.values = [...DEFAULT_EMPLOYEES_ORGANIZATION_FILTERS];
    }
  };

  const STATUS_FILTER_LABELS = {
    draft: "Draft",
    active: "Active",
    terminated: "Terminated",
  };

  const getFilterDisplayLabel = (value) => {
    if (!value) return value;
    if (STATUS_FILTER_LABELS[value]) {
      return STATUS_FILTER_LABELS[value];
    }
    // ER-10492 / MDP-551 radio filter groups (Occupies / Profile Picture / Access / Prepares).
    const radioLabel = getEmployeeRadioFilterLabel(value);
    if (radioLabel) {
      return radioLabel;
    }
    for (const group of state.filters.filterGroups) {
      const item = (group.items || []).find((entry) => entry.value === value);
      if (item) {
        return item.label;
      }
    }
    return value;
  };

  const getFilterDisplayLabels = (values) => {
    return (values || []).map((value) => getFilterDisplayLabel(value));
  };

  const getFilterValueFromLabel = (labelOrValue) => {
    if (!labelOrValue) return labelOrValue;
    if ((state.filters.values || []).includes(labelOrValue)) {
      return labelOrValue;
    }
    const statusValue = Object.entries(STATUS_FILTER_LABELS).find(
      ([, label]) => label === labelOrValue
    );
    if (statusValue) {
      return statusValue[0];
    }
    // ER-10492 / MDP-551 radio filter groups (Occupies / Profile Picture / Access / Prepares).
    const radioValue = getEmployeeRadioFilterValueFromLabel(labelOrValue);
    if (radioValue) {
      return radioValue;
    }
    for (const group of state.filters.filterGroups) {
      const item = (group.items || []).find((entry) => entry.label === labelOrValue);
      if (item) {
        return item.value;
      }
    }
    return labelOrValue;
  };

  /**
   * Single filter -> query-param mapper (ER-11654 §0a/§0b). Rebuilds every API query filter
   * from the complete UI state: status, archived, assignments, the radio groups (primary /
   * preparing-for position, access, profile picture), search text, multi-field search and
   * department. Both the filter Apply path and the search box go through here, so neither
   * owns its own copy of the mapping and `has_access` is only ever set once.
   */
  const syncEmployeeApiFiltersFromState = () => {
    employees.clearEmployeeDynamicFilters();

    const statusFilters = [];

    const searchText = state.filters.searchText?.trim() || "";
    const lowerSearch = searchText.toLowerCase();
    const searchIsNoAccess = lowerSearch === "no access" || lowerSearch === "no_access";

    // The radio groups own `has_access`, `occupies`, `prepares` and `profile_picture`.
    const radioApiFilters = employeeRadioFiltersToApi(state.filters.values);
    if (searchIsNoAccess) {
      radioApiFilters.has_access = false;
    }
    const hasNoAccessFilter = radioApiFilters.has_access === false;

    // ER-11658: Archived is resolved once, off the two stable values, via the shared mapper.
    const archivedValue = resolveArchivedFilterValue(state.filters.values);
    const archivedFilterApplied = archivedValue !== undefined;
    if (archivedFilterApplied) {
      employees.addEmployeeFilter("archived", archivedValue);
    }

    state.filters.values.forEach((el) => {
      const lowerEl = typeof el === "string" ? el.toLowerCase() : el;
      if (
        lowerEl === "draft" ||
        lowerEl === "active" ||
        lowerEl === "terminated"
      ) {
        if (!statusFilters.includes(lowerEl)) {
          statusFilters.push(lowerEl);
        }
      }
      if (lowerEl === "no assignments" || lowerEl === "no_assignment") {
        employees.addEmployeeFilter("has_assignment", false);
      }
      if (lowerEl === "has assignments" || lowerEl === "has_assignment") {
        employees.addEmployeeFilter("has_assignment", true);
      }
    });

    Object.entries(radioApiFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        employees.addEmployeeFilter(key, value);
      }
    });

    // "No access" employees are only meaningful for active ones; typing it in the search
    // box narrows to active employees without access.
    if (searchIsNoAccess) {
      statusFilters.length = 0;
      statusFilters.push("active");
    } else if (hasNoAccessFilter && statusFilters.length === 0) {
      statusFilters.push("active");
    }

    statusFilters.forEach((status) => {
      employees.addEmployeeFilter("status", status);
    });

    if (!archivedFilterApplied) {
      employees.addEmployeeFilter("archived", false);
    }

    if (searchText && !searchIsNoAccess) {
      employees.addEmployeeFilter("name", searchText);
    }

    // Multi-field search (ER-10611): preserve active multi-field search filters so a
    // pagination/filter reload does not drop them (syncEmployeeApiFiltersFromState starts
    // by clearing all dynamic filters).
    (state.filters.multipleFieldSearchValues || []).forEach((field) => {
      if (field.fieldKey && field.value && field.value.trim() !== "") {
        employees.addEmployeeFilter(field.fieldKey, field.value);
      }
    });

    // Department filter (MDP-440): combined via AND with the filters above.
    if (state.filters.department?.id != null) {
      employees.addEmployeeFilter("department", state.filters.department.id);
      employees.addEmployeeFilter(
        "department_scope",
        state.filters.departmentScope || "subtree"
      );
    }
  };

  /** Update + persist the department filter (object) and scope ("direct" | "subtree"). */
  const setDepartmentFilter = (department, scope = "subtree") => {
    state.filters.department = department || null;
    state.filters.departmentScope = department ? scope || "subtree" : "subtree";
  };

  const reloadEmployeesPreservingFilters = async (options = {}) => {
    syncEmployeeApiFiltersFromState();
    await employees.loadAllEmployees(options);
  };

  const applyPostTerminateListUpdate = (employeeId) => {
    if (!employeeId) return;
    const filters = (state.filters.values || []).map((f) => {
      const value = typeof f === "object" ? f.value : f;
      return String(value || "").toLowerCase();
    });
    const showTerminated = filters.includes("terminated");
    const terminatedPatch = {
      status: "terminated",
      primary_position: null,
      primary_positions: [],
      preparing_for_positions: [],
    };
    if (showTerminated) {
      employees.updateEmployeeInList(employeeId, terminatedPatch);
    } else {
      employees.removeEmployeeFromList(employeeId);
    }
  };

  const applyFilters = async (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);

    // Skip only an identical repeat of the filter set in flight; a different
    // set must still reload so the newest filter wins.
    const filtersKey = JSON.stringify(newArray);
    if (applyingFiltersKey === filtersKey) {
      return;
    }

    applyingFiltersKey = filtersKey;
    try {
      filter.setFilters({ key: state.filters.key, filters: newArray });
      state.filters.values = newArray;
      employees.setPaginationDataOffset(0);
      syncEmployeeApiFiltersFromState();

      state.filters.showFilterDialog = false;

      await reloadEmployeesPreservingFilters();
    } finally {
      if (applyingFiltersKey === filtersKey) {
        applyingFiltersKey = null;
      }
    }
  };

  const handleEmployeeSort = async (sortData) => {
    const finalSortField = sortData.descending
      ? `-${sortData.field}`
      : `${sortData.field}`;
    employees.setOrderingDataField(finalSortField);
    await reloadEmployeesPreservingFilters();
  };

  const removeFilters = async (removedFilters) => {
    const removedValue = getFilterValueFromLabel(
      typeof removedFilters === "object" ? removedFilters.value : removedFilters
    );
    state.filters.values = state.filters.values.filter((value) => value !== removedValue);
    employees.clearEmployeeDynamicFilters();
    employees.setPaginationDataOffset(0);
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedValue;
    await applyFilters(
      state.filters.values.map((value) => ({
        label: getFilterDisplayLabel(value),
        value,
      }))
    );
  };

  const removeAllFiltersAndReload = async () => {
    employees.clearEmployeeDynamicFilters();
    employees.addEmployeeFilter("archived", false);
    employees.setPaginationDataOffset(0);

    // ER-11195: "Clear all" restores the documented defaults (draft/active)
    // from the central registry instead of an unfiltered list.
    state.filters.values = filter.resetToDefaults(state.filters.key);
    state.filters.searchText = "";
    state.filters.multipleFieldSearchValues = [];
    state.filters.searchGhostText = EMPLOYEE_SEARCH_PLACEHOLDER;
    setDepartmentFilter(null);

    await employees.loadAllEmployees();
  };

  /**
   * Key of the dashboard tile whose full filter combination is applied (neutral values ignored),
   * else null. Any search text or department filter is an extra filter, so no tile matches.
   * Built in a detached scope: this view state outlives the component that first called it, and a
   * computed bound to that component's scope stops updating once the component is destroyed.
   */
  const activeIndicatorKey = effectScope(true).run(() =>
    computed(() => {
      const hasSearch =
        String(state.filters.searchText || "").trim() !== "" ||
        (state.filters.multipleFieldSearchValues || []).some(
          (f) => String(f?.value ?? "").trim() !== ""
        );
      return state.filters.department?.id != null || hasSearch
        ? null
        : matchIndicatorKey(
            state.filters.values,
            EMPLOYEE_INDICATOR_FILTERS,
            EMPLOYEE_INDICATOR_NEUTRAL_VALUES
          );
    })
  );

  /**
   * ER-11654: click a dashboard tile = replace. Search text, multi-field search and the
   * department filter are cleared and only that tile's combination is applied. Clicking
   * the active tile again resets to the default filters.
   */
  const selectIndicator = async (key) => {
    const target = EMPLOYEE_INDICATOR_FILTERS[key];
    if (!target) return;
    const isActive = activeIndicatorKey.value === key;

    state.filters.searchText = "";
    state.filters.multipleFieldSearchValues = [];
    state.filters.searchGhostText = getEmployeeSearchPlaceholder();
    setDepartmentFilter(null);

    const nextFilters = isActive ? filter.resetToDefaults(state.filters.key) : [...target];
    await applyFilters(nextFilters);
  };

  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string) => {
    // Search text is just another filter dimension: record it, then rebuild the query from
    // the full state through the one shared mapper so the radio filters are preserved.
    state.filters.searchText = string;
    // Simple search replaces any active multi-field search (ER-10611).
    state.filters.multipleFieldSearchValues = [];
    syncEmployeeApiFiltersFromState();
    employees.setPaginationDataOffset(0);
    await employees.loadAllEmployees();
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string) => {
    const queryString = string;
    debouncedSearch(queryString);
  };

  const handleRequest = async ({ page, itemsPerPage }) => {
    employees.setPaginationDataLimit(itemsPerPage);
    employees.setPaginationDataOffset((page - 1) * itemsPerPage);
    await reloadEmployeesPreservingFilters();
  };

  /**
   * ER-10611: multi-field search (name / primary position / preparing for).
   * `event` is the array of { fieldKey, fieldLabel, value } emitted by the multi-field box.
   * Any field left empty is removed so clearing the box reloads the full list from page 1.
   */
  const handleMultipleFieldSearch = async (event) => {
    EMPLOYEE_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
      employees.removeEmployeeFilter(key);
    });

    // Add filters for fields that have non-empty values
    if (event && Array.isArray(event)) {
      event.forEach((field) => {
        if (field.fieldKey && field.value && field.value.trim() !== "") {
          employees.addEmployeeFilter(field.fieldKey, field.value);
        }
      });
    }

    // Update state with the search values (drives the search box UI + syncEmployeeApiFiltersFromState)
    state.filters.multipleFieldSearchValues = event || [];

    // Reset pagination to page 1
    employees.setPaginationDataOffset(0);
    state.table.currentPage = 1;

    // Load employees with the new filters
    await employees.loadAllEmployees();
  };

  const currentEmployee = computed(
    () =>
      state.assignmentWizard.currentEmployee ||
      state.relieveAssignment.currentEmployee
  );

  const getPrimaryPositionDepartmentName = (employee) => {
    const dept = employee?.primary_position?.department;
    if (!dept) return "";
    if (typeof dept === "string") return dept;
    let current = dept;
    while (current.sub_department) {
      current = current.sub_department;
    }
    return current.name || "";
  };

  /** idea-MDP-450: only the employee themselves and administrators may change a picture from the list. */
  const canChangeEmployeePicture = (employee) => {
    if (!state.featureFlags.avatarOpensChangePicture) return false;
    const { user, accessPermission, accountId } = parseStoredAuthContext();
    if (normalizeAdminPermission(accessPermission).hasElevatedNav) return true;
    const ownEmployeeId = resolveAuthEmployeeId({ user, accessPermission, accountId });
    return (
      isValidEmployeeId(employee?.id) &&
      ownEmployeeId != null &&
      Number(employee.id) === Number(ownEmployeeId)
    );
  };

  const getEmployeeCardProps = (employee) => {
    const isAvailabilitySelectorEnabled =
      state.featureFlags.employeeAvailabilitySelector;

    const hideAvailabilitySelector =
      !isAvailabilitySelectorEnabled || employee?.primary_position || false;

    // Try multiple possible ID fields
    const employeeId = employee?.id || employee?.employee_id || employee?.account_employee_id || null;

    const statusLower =
      employee?.status != null ? String(employee.status).toLowerCase() : null;
    const inactive =
      statusLower != null && statusLower !== "active";

    return {
      firstName: employee.first_name,
      lastName: employee.last_name,
      photoUrl: employee.avatar,
      availability: employee.availability,
      position: employee?.primary_position?.name,
      department: getPrimaryPositionDepartmentName(employee),
      showAvailability: true,
      nameUrl: null, // Don't use nameUrl, we'll handle navigation via query params (drilldown)
      showLinkUnderline: false,
      hideAvailabilitySelector: hideAvailabilitySelector,
      isAllocated: !!employee?.primary_position,
      emitFunctionToComposable: true,
      employeeId: employeeId, // Pass employeeId for navigation
      inactive,
      isHoverEnabled: !inactive,
      pictureEditable: canChangeEmployeePicture(employee),
    };
  };

  const getEmployeeAssigmentTagProps = {
    getTypeAssignmentTag: (positionData) => {
      if (!positionData) return "not allowed";
      if (!positionData?.occupied_by) {
        return "vacant";
      } else if (positionData?.occupied_by) {
        return "occupied";
      } else if (positionData?.blocked) {
        return "blocked";
      } else {
        return "not allowed";
      }
    },
    getBasicInfo: (item) => ({
      employeeId: item.id != null && item.id !== "" ? String(item.id) : "",
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
    }),

    getPositionInfo: (item) => ({
      positionCode: item.primary_position?.code_str?.split(" ")[0] || null,
      positionName: item.primary_position?.name,
      effective_date: item.primary_position?.effective_on,
      rolesCount: item.primary_position?.roles_count,
      responsibilityCount: item.primary_position?.responsibilities_count,
      fte: item.primary_position?.allocation || null,
      readiness: item.primary_readiness || 0,
      employeeLoading:
        item.primary_position?.employee_loading === 1 ? false : true,
    }),

    getTypeInfo: (item) => ({
      type:
        item?.status !== "active"
          ? "not allowed"
          : item?.primary_position
          ? getEmployeeAssigmentTagProps.getTypeAssignmentTag(
              item.primary_position
            )
          : "no primary assignment",
    }),
  };

  const getEmployeePrepareAssignmentCardProps = {
    getTypeAssignmentTag: (positionData) => {
      if (!positionData) return "not allowed";
      if (positionData.occupied_by) {
        return "occupied";
      } else if (positionData.occupied_by === null) {
        return "vacant";
      } else if (positionData.status === "inactive") {
        return "inactive";
      } else {
        return "not allowed";
      }
    },
    getOccupiedByInfo: (item) => ({
      occupiedList: item?.preparing_for_positions?.[0]?.occupied_by
        ? [
            {
              firstName: item.preparing_for_positions[0].occupied_by.first_name,
              lastName: item.preparing_for_positions[0].occupied_by.last_name,
              photoUrl: item.preparing_for_positions[0].occupied_by.avatar,
              addHoverEffect: true,
            },
          ]
        : [],
    }),
    getEmployeeInfo: (item) => ({
      firstName: item.first_name,
      lastName: item.last_name,
      photoUrl: item.avatar,
      addHoverEffect: true,
      nameUrl: `employees/${item.id}/pathtoreadiness`,
    }),

    getPositionInfo: (item) => ({
      positionCode:
        item.preparing_for_positions?.[0]?.code_str?.split(" ")[0] || null,
      positionName: item.preparing_for_positions?.[0]?.name,
      effective_date: item.preparing_for_positions?.[0]?.effective_on,
      fte:
        item.preparing_for_positions?.length > 0
          ? item.preparing_for_positions[0].allocation
          : null,
      roles: item.preparing_for_positions?.[0]?.roles_count || 0,
      responsibilities:
        item.preparing_for_positions?.[0]?.responsibilities_count || 0,
      readiness: item.preparing_for_readiness || 0,
      employeeLoading:
        item.preparing_for_positions?.[0]?.employee_loading === 0
          ? false
          : true,
    }),
    getTypeInfo: (item) => ({
      type:
        item?.status !== "active"
          ? "not allowed"
          : item?.preparing_for_positions?.length > 0
          ? getEmployeePrepareAssignmentCardProps.getTypeAssignmentTag(
              item.preparing_for_positions[0]
            )
          : "no prepare assignment",
    }),
  };

  const getStateBadgeProps = (employee) => {
    return {
      access: getEmployeeAccess(employee?.access),
      status: employee.status,
      archived: employee?.archived || false,
      user: employee?.user || null,
      invitedDate: employee?.user?.last_invited_at || employee?.last_invited_at || null,
      inviteResend: Boolean(employee?.user?.invite_resend || employee?.invite_resend),
    };
  };

  const getEmployeeAccess = (access) => {
    if (access?.is_suspended) {
      return "suspended";
    } else {
      return access ? access.employee_status : "no-access";
    }
  };

  const checkUserEmail = async ({ accountId, email }) => {
    state.giveAccessModal.showEmailLoadingIcon = true;
    const response = await employees.checkEmailUser({ accountId, email });
    state.giveAccessModal.showEmailLoadingIcon = false;
    const m = mapCheckEmailApiToLegacyModalState(response, {
      getFirst: () => employees.getCurrentEmployee()?.first_name,
      getLast: () => employees.getCurrentEmployee()?.last_name,
    });
    state.giveAccessModal.dataType = m.dataType;
    if (Object.prototype.hasOwnProperty.call(m, "existingUser")) {
      state.giveAccessModal.existingUser = m.existingUser;
    }
    if (Object.prototype.hasOwnProperty.call(m, "access_level")) {
      state.giveAccessModal.access_level = m.access_level;
    }
  };

  const giveAccessToEmployee = async ({ accountId, payload }) => {
    proxy.$loader.setAppLoading(true);

    try {
      await employees.giveAccessToEmployee({ accountId, payload });
      await reloadEmployeesPreservingFilters();
      requestAccessPermissionSync(proxy.$root);
      proxy.$loader.setAppLoading(false);
      state.giveAccessModal.show = false;
      state.giveBackAccessModal.show = false;
    } catch (error) {
      proxy.$loader.setAppLoading(false);

      let errorMessage =
        "An error occurred while giving access to the employee.";

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.account_permission?.non_field_errors) {
          errorMessage =
            "You can't give access to the owner's email. Only the account owner can do that.";
        }
      }

      proxy.$notifications?.error(errorMessage);
    }
  };

  const openAssignmentWizard = async () => {
    state.assignmentWizard.rejectionMessage = "";
    state.assignmentWizard.effectiveFrom = null;
    state.assignmentWizard.effectiveTo = null;
    state.primaryPositions.currentSearchTerm = "";
    try {
      state.primaryPositions.equalVacancy =
        (await proxy.$getFlag(LD_FLAG_IDEA_MDP_647, false)) === true;
    } catch (error) {
      state.primaryPositions.equalVacancy = false;
    }
    getListOfPrimaryPositions();
    state.assignmentWizard.show = true;
    state.assignmentWizard.selectedPosition = null;
  };

  const openAssignmentPosition = async (position, accountID) => {
    // Position details modal logic
  };

  const openPreparePositionDialog = async (position, accountID) => {
    try {
      proxy.$loader.show();
      state.assignmentPosition.show = true;

      // Handle both primary positions and prepare positions
      // Note: 'position' parameter is actually the employee object
      let positionData = null;
      let positionId = null;

      // Check if it's a prepare position
      if (position?.preparing_for_positions && position.preparing_for_positions.length > 0) {
        positionData = position.preparing_for_positions[0];
        // For prepare positions, the position ID is typically in the 'position' field
        // This is the position assignment, and 'position' field contains the actual position ID
        positionId = positionData?.position;
      }
      // Check if it's a primary position (object)
      else if (position?.primary_position) {
        positionData = position.primary_position;
        // For primary position assignments, the structure might vary:
        // - If it's an assignment object, 'position' field contains the position ID
        // - If it's already a position object, 'id' field contains the position ID
        // - Sometimes it might be 'position_id'
        positionId = positionData?.position || positionData?.id || positionData?.position_id;
        
        // If positionId is still an object, try to extract ID from it
        if (positionId && typeof positionId === 'object') {
          positionId = positionId?.id || positionId?.position;
        }
      }
      // Check if it's primary_positions array
      else if (position?.primary_positions && position.primary_positions.length > 0) {
        positionData = position.primary_positions[0];
        // Same logic as primary_position above
        positionId = positionData?.position || positionData?.id || positionData?.position_id;
        
        // If positionId is still an object, try to extract ID from it
        if (positionId && typeof positionId === 'object') {
          positionId = positionId?.id || positionId?.position;
        }
      }
      
      if (!positionData) {
        proxy.$loader.hide();
        proxy.$notifications?.error("Position data is not available.");
        state.assignmentPosition.show = false;
        return;
      }

      if (!positionId) {
        // Log the structure to help debug
        console.error("Position ID not found. Position data structure:", positionData);
        proxy.$loader.hide();
        proxy.$notifications?.error("Position ID is missing. Cannot load position details.");
        state.assignmentPosition.show = false;
        return;
      }

      // Ensure positionId is a number/string, not an object
      if (typeof positionId === 'object') {
        console.error("Position ID is an object, not a primitive:", positionId);
        positionId = positionId?.id || positionId?.position;
      }

      if (!positionId) {
        proxy.$loader.hide();
        proxy.$notifications?.error("Invalid position ID format.");
        state.assignmentPosition.show = false;
        return;
      }

      const fullPositionData = await getPositionId(
        accountID,
        positionId
      );

      if (!fullPositionData?.data) {
        proxy.$loader.hide();
        proxy.$notifications?.error("Failed to load position details.");
        state.assignmentPosition.show = false;
        return;
      }

      state.assignmentPosition.currentPosition = {
        ...positionData,
        ...fullPositionData.data,
        attachments: fullPositionData.data?.attachments || [],
      };

      state.rolesList = await positions.getRolesById({ accountId: accountID });
      state.responsibityList = await positions.getResponsibilityByAttachRoles({
        accountId: accountID,
      });
      state.sanitizedItems = getSanitizedItems();
      state.assignmentItems = paginatedAttachedRoles(state.sanitizedItems, 1);
      proxy.$loader.hide();
    } catch (error) {
      proxy.$loader.hide();
      state.assignmentPosition.show = false;
      
      if (error.response?.status === 404) {
        proxy.$notifications?.error("Position not found. It may have been deleted or you don't have access to it.");
      } else {
        proxy.$notifications?.error("An error occurred while loading position details.");
      }
      console.error("Error in openPreparePositionDialog:", error);
    }
  };

  const getSanitizedItems = () => {
    // Implementation from original
    return [];
  };

  const paginatedAttachedRoles = (sanitizedItems, page) => {
    // Implementation from original
    return [];
  };

  const openPreparePosition = () => {
    getListOfPreparePositions();
    state.preparePosition.show = true;
    state.preparePosition.selectedPosition = null;
  };

  const closeAssignmentWizard = () => {
    state.assignmentWizard.show = false;
    state.assignmentWizard.selectedPosition = null;
  };

  const closePreparePosition = () => {
    state.preparePosition.show = false;
    state.preparePosition.selectedPosition = null;
  };

  const closeRelieveAssignment = () => {
    state.relieveAssignment.show = false;
    state.relieveAssignment.currentPosition = null;
    employees.setCurrentEmployee(null);
    state.relieveAssignment.confirmationPending = false;
  };

  const relievePrimaryPositionFromEmployee = async (accountID, data) => {
    proxy.$loader.setAppLoading(true);
    const employee_id = employees.getCurrentEmployee()?.id;
    const assignmentId = state.relieveAssignment.currentPosition?.id;
    const payload = {
      effective_to: data,
    };
    try {
      await employees.removePositionAssignmentToEmployee({
        accountId: accountID,
        assignment_id: assignmentId,
        employee_id: employee_id,
        data: payload,
      });
    } catch (error) {
      proxy.$loader.setAppLoading(false);
      const responseData = error?.response?.data;
      const errorMessage =
        (Array.isArray(responseData) && responseData.join(" ")) ||
        responseData?.detail ||
        (typeof responseData === "string" && responseData) ||
        "Failed to relieve the position assignment. Please try again.";
      proxy.$notifications?.error(errorMessage);
      return;
    }

    state.relieveAssignment.show = false;
    employees.setCurrentEmployee(null);
    proxy.$loader.setAppLoading(false);
    proxy?.$notifications?.success("Position unassigned successfully");

    await reloadEmployeesPreservingFilters();
    await positions.loadAllPositions();
    if (employee_id) {
      try {
        await recalculateEmployeeReadiness(accountID, employee_id);
      } catch (e) {
        console.error("error: ", e);
      }
    }
  };

  const handleEmployeeAvatarClick = (employee) => {
    if (!canChangeEmployeePicture(employee)) return;
    return handleEmployeeDropdownOperation({ value: "change_picture_action" }, employee);
  };

  const handleEmployeeDropdownOperation = async (operation, employee) => {
    state.currentRow = employee;
    if (!operation || !employee) return;
    employees.setCurrentEmployee(employee);
    switch (operation.value) {
      case "activate_action":
        state.activeEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee()?.last_name || ""
        } is about to be terminated. We will relieve the employee of all their assignments. We will also suspend access for the employee to this account.`;
        state.activeEmployeeModal.show = true;
        break;
      case "delete_action":
        state.deleteEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee().last_name || ""
        } is about to be Deleted. This action is irreversible.`;
        state.deleteEmployeeModal.show = true;
        break;
      case "edit_action":
        state.addEmployeeModal.show = true;
        break;
      case "change_picture_action":
        state.changePictureModal.show = true;
        break;
      case "give_access_action":
        state.giveAccessModal.show = true;
        break;
      case "suspend_access_action":
        state.suspendEmployeeAccessModal.show = true;
        state.suspendEmployeeAccessModal.message = `You are about to suspend access for ${
          employees.getCurrentEmployee()?.first_name || ""
        } ${employees.getCurrentEmployee()?.last_name || ""} .`;
        break;
      case "give_back_access_action":
        state.giveBackEmployeeAccessModal.show = true;
        break;
      case "terminate_action":
        state.terminateEmployeeModal.message = `${
          employees.getCurrentEmployee()?.first_name || ""
        } ${
          employees.getCurrentEmployee()?.last_name || ""
        } is about to be terminated. We will relieve the employee of all their assignments. We will also suspend access for the employee to this account.`;
        state.terminateEmployeeModal.show = true;
        break;
      case "reactivate_action":
        state.reactivateEmployeeModal.show = true;
        break;
      case "archive_action":
        state.archiveAction = "archive";
        state.archiveEmployeeModal.show = true;
        break;
      case "unarchive_action":
        state.archiveAction = "unarchive";
        state.archiveEmployeeModal.show = true;
        break;
      case "resend_invite_action":
        state.resendInviteModal.show = true;
        break;
      case "cancel_invite_action":
        state.cancelInviteModal.show = true;
        break;
      case "impersonate_action":
        await requestViewAsOrPreview(MODE_VIEW_AS, employee);
        break;
      case "preview_as_employee_action":
        await requestViewAsOrPreview(MODE_PREVIEW, employee);
        break;
      default:
        break;
    }
  };

  const requestViewAsOrPreview = async (mode, employee) => {
    // Match EmployeeLineMenu eligibility (shared LD flag + live admin check).
    // Listing menus can show the action before viewState flag fields catch up.
    const enabled = state.viewAsFeatureEnabled || viewAsLdEnabled.value;
    const canStart =
      state.canStartViewAs ||
      refreshCanStartViewAs() ||
      currentUserCanStartViewAs();
    if (!enabled || !canStart) return;

    const targetStatus = getViewAsTargetStatus({
      ...employee,
      accessStatus: getEmployeeAccess(employee?.access),
      isSuspended: !!(employee?.access?.is_suspended),
    });

    if (mode === MODE_VIEW_AS && targetStatus) {
      state.viewAsConfirmModal = {
        show: true,
        message: buildViewAsConfirmMessage(employee, targetStatus),
        mode,
        targetStatus,
        employee,
      };
      return;
    }

    await enterViewAsOrPreview(mode, employee, targetStatus);
  };

  const enterViewAsOrPreview = async (mode, employee, targetStatus = "") => {
    try {
      proxy?.$loader?.setAppLoading?.(true);
      await startAndEnterViewAsOrPreview({
        mode,
        employee,
        targetStatus,
        startApi:
          mode === MODE_PREVIEW
            ? ({ employeeId }) => startPreview({ employeeId })
            : ({ accessId }) => startViewAs({ accessId }),
        exchangeApi: mode === MODE_PREVIEW ? exchangePreview : exchangeViewAs,
      });
    } catch (error) {
      console.error("Failed to start view-as/preview:", error);
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to start session. Please try again.";
      proxy?.$notifications?.error?.(message);
    } finally {
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  const confirmViewAsAfterStatusWarning = async () => {
    const { mode, employee, targetStatus } = state.viewAsConfirmModal;
    state.viewAsConfirmModal.show = false;
    if (!mode || !employee) return;
    await enterViewAsOrPreview(mode, employee, targetStatus);
  };

  // Same user-id resolution as employee detail page (user-based resend/cancel invite API)
  const getInviteUserId = (emp) => {
    if (!emp) return null;
    return (
      emp?.user?.id ??
      (typeof emp?.user === "number" ? emp.user : null) ??
      emp?.access?.user_id ??
      emp?.access?.user?.id ??
      (typeof emp?.access?.user === "number" ? emp?.access?.user : null) ??
      emp?.user_id ??
      null
    );
  };

  const getInviteUserIdFromList = (employeeId) => {
    if (!employeeId) return null;
    const id = Number(employeeId);
    const list = employees.allEmployees?.value ?? employeeStore?.getEmployees ?? [];
    const row = [].concat(list).find((emp) => Number(emp?.id) === id);
    return row ? getInviteUserId(row) : null;
  };

  const getInviteUserIdFromListFetch = async (employeeId, accountId) => {
    if (!employeeId || !accountId) return null;
    const id = Number(employeeId);
    try {
      const resById = await getListofEmployeesV2({
        account_id: accountId,
        queryParamsObj: { limit: 1, id },
      });
      const listById = resById?.data?.results || [];
      const rowById = listById.find((emp) => Number(emp?.id) === id);
      if (rowById) {
        const uid = getInviteUserId(rowById);
        if (uid) return uid;
      }
      const res = await getListofEmployeesV2({
        account_id: accountId,
        queryParamsObj: { limit: 500, offset: 0 },
      });
      const list = res?.data?.results || [];
      const row = list.find((emp) => Number(emp?.id) === id);
      return row ? getInviteUserId(row) : null;
    } catch (e) {
      console.warn("Could not resolve user_id from employees list:", e);
      return null;
    }
  };

  const getInviteUserIdFromAccessList = async (email, accountId) => {
    if (!email || !accountId) return null;
    try {
      const res = await getListofUser(accountId);
      const results = res?.data?.results || res?.data || [];
      const row = [].concat(results).find(
        (item) =>
          (item?.user?.data?.user?.email || item?.user?.email || item?.email || "").toLowerCase() ===
          String(email).toLowerCase()
      );
      const uid = row?.user?.id ?? row?.user?.data?.user?.id ?? row?.id;
      return uid ? Number(uid) : null;
    } catch (e) {
      console.warn("Could not resolve user_id from access list:", e);
      return null;
    }
  };

  const resolveInviteUserId = async (employee, accountId) => {
    let userId = getInviteUserId(employee);
    const employeeId = employee?.id;
    if (!userId && employeeId) {
      const fetched = await employeeStore?.fetchCurrentEmployee?.(employeeId, true);
      if (fetched) userId = getInviteUserId(fetched);
    }
    if (!userId && employeeId) userId = getInviteUserIdFromList(employeeId);
    if (!userId && employeeId) userId = await getInviteUserIdFromListFetch(employeeId, accountId);
    if (!userId && employeeId) {
      const email = employee?.email;
      if (email) userId = await getInviteUserIdFromAccessList(email, accountId);
    }
    return userId;
  };

  const handleResendInvite = async () => {
    proxy?.$loader?.setAppLoading?.(true);
    try {
      const employee = employees.getCurrentEmployee();
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      if (!employee?.id) {
        proxy?.$notifications?.error?.("Employee not found.");
        state.resendInviteModal.show = false;
        return;
      }
      const userId = await resolveInviteUserId(employee, accountId);
      if (!userId) {
        proxy?.$notifications?.error?.(
          "User ID not found for this employee. Try Resend Invite from the employee detail or Settings instead."
        );
        state.resendInviteModal.show = false;
        return;
      }
      await userResendInvite(userId, { user: userId, account: accountId });
      proxy?.$notifications?.success?.("Invite resent successfully.");
      state.resendInviteModal.show = false;
      await reloadEmployeesPreservingFilters();
    } catch (e) {
      console.error("Error resending invite:", e);
      const message = e?.response?.data?.detail || "Failed to resend invite. Please try again.";
      proxy?.$notifications?.error?.(message);
    } finally {
      state.resendInviteModal.show = false;
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  const handleCancelInvite = async () => {
    proxy?.$loader?.setAppLoading?.(true);
    try {
      const employee = employees.getCurrentEmployee();
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      if (!employee?.id) {
        proxy?.$notifications?.error?.("Employee not found.");
        state.cancelInviteModal.show = false;
        return;
      }
      const userId = await resolveInviteUserId(employee, accountId);
      if (!userId) {
        proxy?.$notifications?.error?.(
          "User ID not found for this employee. Try Cancel Invite from the employee detail or Settings instead."
        );
        state.cancelInviteModal.show = false;
        return;
      }
      await cancelInviteUser(userId);
      proxy?.$notifications?.success?.("Invite cancelled successfully.");
      state.cancelInviteModal.show = false;
      await reloadEmployeesPreservingFilters();
    } catch (e) {
      console.error("Error cancelling invite:", e);
      const message = e?.response?.data?.detail || "Failed to cancel invite. Please try again.";
      proxy?.$notifications?.error?.(message);
    } finally {
      state.cancelInviteModal.show = false;
      proxy?.$loader?.setAppLoading?.(false);
    }
  };

  const handleEmployeeOperation = async (
    operation,
    accountId,
    payload = null,
    { stayOnList = false } = {}
  ) => {
    try {
      proxy.$loader.setAppLoading(true);
      if (operation == employees.operations.TERMINATE_EMPLOYEE) {
        const terminatedEmployee = employees.getCurrentEmployee();
        await employees.terminate({
          accountId: accountId,
          employee: terminatedEmployee,
          data: payload,
        });
        applyPostTerminateListUpdate(terminatedEmployee?.id);
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await recalculateEmployeeReadiness(
          accountId,
          terminatedEmployee?.id
        );
        state.terminateEmployeeModal.show = false;
      } else if (operation == employees.operations.ACTIVATE_EMPLOYEE) {
        await employees.activate({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await reloadEmployeesPreservingFilters();
        state.activeEmployeeModal.show = false;
      } else if (operation == employees.operations.DELETE_EMPLOYEE) {
        await employees.deleteEmp({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await reloadEmployeesPreservingFilters();
        state.deleteEmployeeModal.show = false;
      } else if (operation == employees.operations.REACTIVATE_EMPLOYEE) {
        await employees.reactivate({
          accountId,
          employee: employees.getCurrentEmployee(),
        });
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await reloadEmployeesPreservingFilters();
        state.reactivateEmployeeModal.show = false;
      } else if (operation == employees.operations.EDIT_EMPLOYEE) {
        if (!payload) return;
        const newEmployee = await employees.saveEmployee({
          accountId,
          employee: payload,
          allData: employees.getCurrentEmployee(),
        });
        if (!payload?.id) {
          if (newEmployee) {
            const { data } = newEmployee;
            
            // Navigate to employee detail page using query parameters to show in drilldown mode
            if (data?.id) {
              state.addEmployeeModal.show = false;
              proxy.$loader.setAppLoading(false);

              // ER-12353: first employee added from the empty-account state stays on the list.
              if (stayOnList) {
                await reloadEmployeesPreservingFilters();
                return { createdEmployee: data };
              }

              // Wait for modal to close and DOM to update
              await proxy.$nextTick();
              
              // Refresh table data before navigating to ensure new employee appears when user returns
              await reloadEmployeesPreservingFilters();
              
              // Navigate first
              await proxy.$router.push({
                name: 'NewOrganization',
                params: { tab: 'employees' },
                query: {
                  employeeId: String(data.id),
                  employeeTab: getOrganizationEmployeeDefaultQueryTab(),
                }
              }).catch(() => {});
              
              // Return employee data so component can emit drilldown event
              return { createdEmployee: data };
            }
            state.addEmployeeModal.show = false;
            state.filters.searchText = `${data.first_name} ${data.last_name}`;
            employees.setPaginationDataOffset(0);
            await reloadEmployeesPreservingFilters();
            setTimeout(() => {
              highlight({ id: newEmployee.data.id, type: "Employees" });
            }, 500);
          }
          state.addEmployeeModal.show = false;
        } else {
          await reloadEmployeesPreservingFilters();
          state.addEmployeeModal.show = false;
        }
      } else if (operation == employees.operations.CHANGE_EMPLOYEE_PICTURE) {
        const newEmployee = {
          id: employees.getCurrentEmployee()?.id,
          first_name: employees.getCurrentEmployee()?.first_name,
          last_name: employees.getCurrentEmployee()?.last_name,
          title: employees.getCurrentEmployee()?.title,
          avatar: payload,
        };
        const response = await employees.saveEmployeeWithAvatar({
          accountId,
          employee: newEmployee,
        });

        const currentEmp = employees.getCurrentEmployee();
        if (currentEmp && response?.data) {
          const updatedEmployee = {
            ...currentEmp,
            ...response.data,
          };
          employees.setCurrentEmployee(updatedEmployee);
        }

        if (response?.data && response?.data?.avatar !== undefined) {
          updateUserAvatarInStorage(response.data, response.data.avatar || "");
        }

        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await reloadEmployeesPreservingFilters();
      } else if (operation == employees.operations.ARCHIVE_EMPLOYEE) {
        if (state.archiveAction === "archive") {
          await employees.archiveEmp({
            accountId: accountId,
            employee: employees.getCurrentEmployee(),
          });
        } else if (state.archiveAction === "unarchive") {
          await employees.unArchiveEmp({
            accountId: accountId,
            employee: employees.getCurrentEmployee(),
          });
        }
        const count = employees.allEmployees.value.length;
        if (count == 1) {
          employees.setPaginationDataOffset(0);
        }
        await reloadEmployeesPreservingFilters();
        state.archiveEmployeeModal.show = false;
      }
      if (operation != employees.operations.CHANGE_EMPLOYEE_PICTURE) {
        employees.setCurrentEmployee(null);
      }
    } catch (e) {
      console.error("error on employee operation: ", e);
    } finally {
      proxy.$loader.setAppLoading(false);
    }
  };

  const clearSearch = () => {
    state.filters.searchText = "";
    state.filters.multipleFieldSearchValues = [];
    EMPLOYEE_LIST_SEARCH_PARAM_KEYS.forEach((key) => {
      employees.removeEmployeeFilter(key);
    });
  };

  const getEmployeeChatterData = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      const currentEmployee = employees.getCurrentEmployee();
      if (!currentEmployee) return;
      
      state.openChatterModal.show = true;
      const response = await getChatterList(
        accountID,
        currentEmployee.id,
        "employee",
        state.openChatterModal.page
      );
      if (response && response.data) {
        let chatterItems = response.data.results.map((el) => {
          const date = new Date(el.recorded_on);
          const milliseconds = date.getTime();
          const millisecondsString = milliseconds.toString();
          let nameParts = el.recorded_by_name.split(" ");
          const newEl = {};
          newEl.id = el.id;
          if (nameParts.length === 2) {
            newEl.avatar = {
              picture: "",
              firstName: nameParts[0],
              lastName: nameParts[1],
            };
          } else {
            newEl.avatar = {
              picture: "",
              firstName: el.recorded_by_name,
              lastName: "",
            };
          }
          newEl.IsExIQtiveBot = false;
          newEl.message = el.note;
          newEl.created_on = millisecondsString;
          newEl.IsHistory = el.is_history;
          return newEl;
        });
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalEmployeeChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };

  const openEmployeeChatterModal = async (employee) => {
    if (employee) {
      employees.setCurrentEmployee(employee);
    }
    state.openChatterModal.chatterData = [];
    state.openChatterModal.page = 1;
    await getEmployeeChatterData();
  };

  const closeEmployeeChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalEmployeeChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };

  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getEmployeeChatterData();
  };

  const onApplyDatePrepareFor = async (date) => {
    proxy.$loader.setAppLoading(true);
    try {
      await employees.onApplyDatePrepareFor({
        employee: employees.getCurrentEmployee(),
        effective_date: date,
      });
      const count = employees.allEmployees.value.length;
      if (count == 1) {
        employees.setPaginationDataOffset(0);
      }
      await reloadEmployeesPreservingFilters();
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const onApplyDatePrimaryAssignment = async (date) => {
    proxy.$loader.setAppLoading(true);
    try {
      await employees.onApplyDatePrimaryAssignment({
        employee: employees.getCurrentEmployee(),
        effective_date: date,
      });
      const count = employees.allEmployees.value.length;
      if (count == 1) {
        employees.setPaginationDataOffset(0);
      }
      await reloadEmployeesPreservingFilters();
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const getListOfPrimaryPositions = async (
    page = 1,
    searchTerm = "",
    isLoadMore = false
  ) => {
    const currentEmployee = employees.getCurrentEmployee();
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";

    if (state.primaryPositions.equalVacancy) {
      await getListOfEqualVacancyPositions(searchTerm, isLoadMore);
      return;
    }

    try {
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      const primaryPositions = await getListOfPositionsForAssignment({
        accountId,
        limit,
        offset,
        name,
      });

      if (!isLoadMore && searchTerm && primaryPositions.data.results.length === 0) {
        state.primaryPositions.searchNoResults = true;
        // Search returned nothing, re-fetch all data as fallback
        const allPositions = await getListOfPositionsForAssignment({
          accountId,
          limit,
          offset: 0,
          name: "",
        });
        
        let allItems = allPositions.data.results.map((item) => ({
          id: item.id,
          code: item.code_str.match(/\d+/) ? item.code_str.match(/\d+/)[0] : item.id,
          code_str: item.code_str,
          name: item.name,
          status: item.status,
          isInvalid: Boolean(item.is_invalid),
          directResponsibilitiesCount: item.direct_responsibilities_count || 0,
          notActiveDirectResponsibilitiesCount: item.not_active_direct_responsibilities_count || 0,
          validRolesCount: item.valid_roles_count || 0,
          invalidRolesCount: item.invalid_roles_count || 0,
          validResponsibilitiesCount: item.active_responsibilities_count || 0,
          capacity: item.capacity || 0,
          allocation: item.allocation === 0 || !item.allocation ? 'not' : String(item.allocation),
          archived: item.archived || false,
          invalidityReason: item.invalidity_reason || "",
          assigned_primary: item.assigned_primary || null,
          occupied_by: item.occupied_by || null,
          primary_occupied_by: item.primary_occupied_by || null,
          disabled: !!(item.primary_occupied_by && item.primary_occupied_by.length > 0) ||
                   !!(item.occupied_by && (Array.isArray(item.occupied_by) ? item.occupied_by.length > 0 : !!item.occupied_by)) ||
                   !!item.assigned_primary ||
                   item.vacant === 0 ||
                   item.vacancy === 0 ||
                   (item.status && String(item.status).toLowerCase() !== 'active')
        }));

        if (currentEmployee?.preparing_for_positions?.length > 0) {
          const preparingCodes = currentEmployee.preparing_for_positions.map(p => p.code_str);
          allItems = allItems.filter(
            (item) => !preparingCodes.includes(item.code_str)
          );
        }

        state.primaryPositions.availablePositions = allItems;
        state.primaryPositions.currentPage = 1;
        state.primaryPositions.totalCount = allPositions.data.count || 0;
        state.primaryPositions.hasMoreData = limit < state.primaryPositions.totalCount;
        return;
      }

      state.primaryPositions.searchNoResults = false;
      let newItems = primaryPositions.data.results.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/) ? item.code_str.match(/\d+/)[0] : item.id,
        code_str: item.code_str,
        name: item.name,
        status: item.status,
        isInvalid: Boolean(item.is_invalid),
        directResponsibilitiesCount: item.direct_responsibilities_count || 0,
        notActiveDirectResponsibilitiesCount: item.not_active_direct_responsibilities_count || 0,
        validRolesCount: item.valid_roles_count || 0,
        invalidRolesCount: item.invalid_roles_count || 0,
        validResponsibilitiesCount: item.active_responsibilities_count || 0,
        capacity: item.capacity || 0,
        allocation: item.allocation === 0 || !item.allocation ? 'not' : String(item.allocation),
        archived: item.archived || false,
        invalidityReason: item.invalidity_reason || "",
        assigned_primary: item.assigned_primary || null,
        occupied_by: item.occupied_by || null,
        primary_occupied_by: item.primary_occupied_by || null,
        disabled: !!(item.primary_occupied_by && item.primary_occupied_by.length > 0) ||
                 !!(item.occupied_by && (Array.isArray(item.occupied_by) ? item.occupied_by.length > 0 : !!item.occupied_by)) ||
                 !!item.assigned_primary ||
                 item.vacant === 0 ||
                 item.vacancy === 0 ||
                 (item.status && String(item.status).toLowerCase() !== 'active')
      }));

      if (currentEmployee?.preparing_for_positions?.length > 0) {
        const preparingCodes = currentEmployee.preparing_for_positions.map(p => p.code_str);
        newItems = newItems.filter(
          (item) => !preparingCodes.includes(item.code_str)
        );
      }

      if (isLoadMore && state.primaryPositions.availablePositions) {
        state.primaryPositions.availablePositions = [
          ...state.primaryPositions.availablePositions,
          ...newItems,
        ];
      } else {
        state.primaryPositions.availablePositions = newItems;
      }

      state.primaryPositions.currentPage = page;
      state.primaryPositions.totalCount = primaryPositions.data.count || 0;
      state.primaryPositions.hasMoreData =
        page * limit < state.primaryPositions.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = false;
      }
    }
  };

  let equalVacancyRequestId = 0;

  const getListOfEqualVacancyPositions = async (searchTerm = "", isLoadMore = false) => {
    const currentEmployee = employees.getCurrentEmployee();
    const requestId = ++equalVacancyRequestId;
    const { effectiveFrom, effectiveTo } = state.assignmentWizard;
    try {
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      if (!isLoadMore) {
        state.primaryPositions.employeeVacancy = await fetchEmployeeVacancy({
          accountId,
          employee: currentEmployee,
          effectiveFrom,
          effectiveTo,
        });
      }
      const result = await loadEqualVacancyPositions({
        accountId,
        name: searchTerm || "",
        offset: isLoadMore ? state.primaryPositions.serverOffset : 0,
        effectiveFrom,
        effectiveTo,
        employeeVacancy: state.primaryPositions.employeeVacancy,
        excludeCodeStrs: (currentEmployee?.preparing_for_positions || []).map(
          (position) => position.code_str
        ),
      });
      if (requestId !== equalVacancyRequestId) return;
      state.primaryPositions.availablePositions = isLoadMore
        ? [...state.primaryPositions.availablePositions, ...result.items]
        : result.items;
      state.primaryPositions.serverOffset = result.nextOffset;
      state.primaryPositions.totalCount = result.totalCount;
      state.primaryPositions.hasMoreData = result.hasMore;
      state.primaryPositions.searchNoResults =
        state.primaryPositions.availablePositions.length === 0 && !result.hasMore;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.primaryPositions.isLoadingMore = false;
      }
    }
  };

  const onAssignmentDateChange = async ({ effectiveFrom, effectiveTo }) => {
    state.assignmentWizard.effectiveFrom = effectiveFrom;
    state.assignmentWizard.effectiveTo = effectiveTo;
    state.assignmentWizard.rejectionMessage = "";
    await getListOfEqualVacancyPositions(state.primaryPositions.currentSearchTerm, false);
  };

  const loadMorePrimaryPositions = async () => {
    if (
      state.primaryPositions.isLoadingMore ||
      !state.primaryPositions.hasMoreData
    ) {
      return;
    }

    const nextPage = state.primaryPositions.currentPage + 1;
    await getListOfPrimaryPositions(
      nextPage,
      state.primaryPositions.currentSearchTerm,
      true
    );
  };

  const searchPrimaryPositions = async (searchTerm) => {
    state.primaryPositions.currentSearchTerm = searchTerm;
    state.primaryPositions.currentPage = 1;
    state.primaryPositions.hasMoreData = true;
    await getListOfPrimaryPositions(
      1,
      state.primaryPositions.currentSearchTerm,
      false
    );
  };

  const getListOfPreparePositions = async (
    page = 1,
    searchTerm = "",
    isLoadMore = false
  ) => {
    const currentEmployee = employees.getCurrentEmployee();
    const limit = 100;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";

    try {
      if (isLoadMore) {
        state.preparePositions.isLoadingMore = true;
      }
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

      const preparePositions = await getListOfPositionsForPrepareAssignment({
        accountId,
        limit,
        offset,
        name,
      });

      if (!isLoadMore && searchTerm && preparePositions.data.results.length === 0) {
        state.preparePositions.searchNoResults = true;
        // Search returned nothing, re-fetch all data as fallback
        const allPositions = await getListOfPositionsForPrepareAssignment({
          accountId,
          limit,
          offset: 0,
          name: "",
        });
        
        let allItems = allPositions.data.results.map((item) => ({
          ...item,
        }));

        if (
          currentEmployee &&
          currentEmployee?.primary_position &&
          currentEmployee?.primary_position?.code_str
        ) {
          const primaryCodeStr = currentEmployee?.primary_position?.code_str;
          allItems = allItems.filter((item) => item.code_str !== primaryCodeStr);
        }

        state.preparePositions.availablePositions = allItems;
        state.preparePositions.currentPage = 1;
        state.preparePositions.totalCount = allPositions.data.count || 0;
        state.preparePositions.hasMoreData = limit < state.preparePositions.totalCount;
        return;
      }

      state.preparePositions.searchNoResults = false;
      let newItems = preparePositions.data.results.map((item) => ({
        ...item,
      }));

      if (
        currentEmployee &&
        currentEmployee?.primary_position &&
        currentEmployee?.primary_position?.code_str
      ) {
        const primaryCodeStr = currentEmployee?.primary_position?.code_str;
        newItems = newItems.filter((item) => item.code_str !== primaryCodeStr);
      }

      if (isLoadMore && state.preparePositions.availablePositions) {
        state.preparePositions.availablePositions = [
          ...state.preparePositions.availablePositions,
          ...newItems,
        ];
      } else {
        state.preparePositions.availablePositions = newItems;
      }

      state.preparePositions.currentPage = page;
      state.preparePositions.totalCount = preparePositions.data.count || 0;
      state.preparePositions.hasMoreData =
        page * limit < state.preparePositions.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.preparePositions.isLoadingMore = false;
      }
    }
  };

  const loadMorePreparePositions = async () => {
    if (
      state.preparePositions.isLoadingMore ||
      !state.preparePositions.hasMoreData
    ) {
      return;
    }

    const nextPage = state.preparePositions.currentPage + 1;
    await getListOfPreparePositions(
      nextPage,
      state.preparePositions.currentSearchTerm,
      true
    );
  };

  const searchPreparePositions = async (searchTerm) => {
    state.preparePositions.currentSearchTerm = searchTerm;
    state.preparePositions.currentPage = 1;
    state.preparePositions.hasMoreData = true;
    await getListOfPreparePositions(
      state.preparePositions.currentPage,
      state.preparePositions.currentSearchTerm,
      false
    );
  };

  const onAssignEqualVacancyPosition = async ({
    position,
    effectiveDate,
    effectiveTo,
    notifyEmployee,
    allocation,
    accountId,
  }) => {
    const currentEmployee = employees.getCurrentEmployee();
    if (!currentEmployee?.id || !position?.id) return;
    state.assignmentWizard.rejectionMessage = "";
    state.assignmentWizard.isAssigning = true;
    const result = await employees.onAssignPositionToEmployee({
      employee: currentEmployee,
      position: {
        ...position,
        effective_on: effectiveDate,
        effective_to: effectiveTo,
        isNotify: notifyEmployee,
      },
      accountID: accountId,
      allocation,
    });
    state.assignmentWizard.isAssigning = false;
    if (!result?.success) {
      state.assignmentWizard.rejectionMessage = getAssignmentRejectionMessage(result?.error);
      return;
    }
    state.assignmentWizard.show = false;
    proxy?.$notifications?.success("Position assigned successfully");
    await reloadEmployeesPreservingFilters();
    await recalculateEmployeeReadiness(accountId, currentEmployee.id);
  };

  const onAssignPositionToEmployee = async (payload) => {
    if (state.primaryPositions.equalVacancy) {
      await onAssignEqualVacancyPosition(payload);
      return;
    }
    await onAssignLegacyPositionToEmployee(payload);
  };

  const onAssignLegacyPositionToEmployee = async ({
    position,
    effectiveDate,
    notifyEmployee,
    accountId,
  }) => {
    proxy.$loader.setAppLoading(true);
    state.assignmentWizard.isAssigning = true;
    if (
      !employees.getCurrentEmployee()?.id ||
      !position?.id ||
      !employees.getCurrentEmployee()?.availability
    ) {
      state.assignmentWizard.isAssigning = false;
      proxy.$loader.setAppLoading(false);
      return;
    }
    try {
      const currentEmployee = employees.getCurrentEmployee();
      if (currentEmployee && state.showAssignmentCreationFlag) {
        const positionWithCodeStr = {
          code_str: position.code_str,
          name: position.name,
          effective_on: effectiveDate,
          roles_count: null,
          responsibilities_count: null,
          allocation: position.allocation || 1,
          employee_loading: 0,
          status: "active",
          occupied_by: {
            first_name: currentEmployee.first_name,
            last_name: currentEmployee.last_name,
            avatar: currentEmployee.avatar,
          },
        };

        const updatedEmployee = {
          ...currentEmployee,
          primary_position: positionWithCodeStr,
        };

        employees.setCurrentEmployee(updatedEmployee);

        const allEmployees = [...employees.allEmployees.value];
        const employeeIndex = allEmployees.findIndex(
          (emp) => emp.id == currentEmployee.id
        );

        if (employeeIndex !== -1) {
          allEmployees[employeeIndex] = updatedEmployee;

          const employeeStore = useEmployeeStore();
          employeeStore.setEmployees(allEmployees);
        }
      }
      if (state.showAssignmentCreationFlag) {
        state.assignmentWizard.show = false;
      }
      const positionWithDetails = {
        ...position,
        effective_on: effectiveDate,
        isNotify: notifyEmployee,
      };
      await employees.onAssignPositionToEmployee({
        employee: employees.getCurrentEmployee(),
        position: positionWithDetails,
        accountID: accountId,
      });

      // Close dialog and show toast immediately for better UX
      state.assignmentWizard.show = false;
      state.assignmentWizard.isAssigning = false;
      proxy.$loader.setAppLoading(false);
      proxy?.$notifications?.success("Position assigned successfully");

      // Reload data immediately for "on time" table refresh
      await reloadEmployeesPreservingFilters();
      await recalculateEmployeeReadiness(
        accountId,
        employees.getCurrentEmployee()?.id
      );
    } catch (e) {
      console.error("error: ", e);
    } finally {
      state.assignmentWizard.show = false;
      state.assignmentWizard.isAssigning = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const onAssignPreparePositionToEmployee = async ({ obj, accountId }) => {
    proxy.$loader.setAppLoading(true);
    state.preparePosition.isAssigning = true;
    if (
      !employees.getCurrentEmployee()?.id ||
      !obj?.position?.id ||
      !employees.getCurrentEmployee()?.availability
    ) {
      state.preparePosition.isAssigning = false;
      proxy.$loader.setAppLoading(false);
      return;
    }
    try {
      const currentEmployee = employees.getCurrentEmployee();
      if (currentEmployee && state.showAssignmentCreationFlag) {
        const positionWithCodeStr = {
          ...obj.position,
          code: obj.position.code_str,
          occupied_by: currentEmployee,
          status: "active",
          effective_on: obj.effective_date,
          allocation: obj.position.allocation || 1,
          roles_count: null,
          responsibilities_count: null,
          employee_loading: 1,
        };

        const updatedEmployee = {
          ...currentEmployee,
          preparing_for_positions: [positionWithCodeStr],
        };

        employees.setCurrentEmployee(updatedEmployee);

        const allEmployees = [...employees.allEmployees.value];
        const employeeIndex = allEmployees.findIndex(
          (emp) => emp.id == currentEmployee.id
        );

        if (employeeIndex !== -1) {
          allEmployees[employeeIndex] = updatedEmployee;
          const employeeStore = useEmployeeStore();
          employeeStore.setEmployees(allEmployees);
        }
      }
      if (state.showAssignmentCreationFlag) {
        state.preparePosition.show = false;
      }
      await employees.onAssignPreparePositionToEmployee({
        employee: employees.getCurrentEmployee(),
        position: obj.position,
        effective_date: obj.effective_date,
        accountID: accountId,
        isNotify: obj.isNotify,
      });

      // Close dialog and show toast immediately for better UX
      state.preparePosition.show = false;
      state.preparePosition.isAssigning = false;
      proxy.$loader.setAppLoading(false);
      proxy?.$notifications?.success("Position assigned successfully");

      // Reload data immediately for "on time" table refresh
      await reloadEmployeesPreservingFilters();
      await recalculateEmployeeReadiness(
        accountId,
        employees.getCurrentEmployee()?.id
      );
    } catch (e) {
      proxy?.$notifications?.error("Failed to assign position");
      console.error("error: ", e);
    } finally {
      state.preparePosition.show = false;
      state.preparePosition.isAssigning = false;
      proxy.$loader.setAppLoading(false);
    }
  };

  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    employees.clearEmployeeDynamicFilters();

    if (!proxy) {
      return;
    }

    try {
      const hideArchivedFilterEmployees = await proxy.$getFlag(
        "hide_archived_filter_employees",
        false
      );

      if (hideArchivedFilterEmployees) {
        // ER-11658: match the stable key, not the heading text — relabelling "Archived"
        // must not silently disable this kill switch.
        state.filters.filterGroups = state.filters.filterGroups.filter(
          (group) => group.key !== "archived"
        );
      }

      const employeeAvailabilitySelector = await proxy.$getFlag(
        "employee_availability_selector",
        true
      );
      state.featureFlags.employeeAvailabilitySelector =
        employeeAvailabilitySelector;
      state.featureFlags.avatarOpensChangePicture =
        (await proxy.$getFlag(LD_FLAG_IDEA_MDP_450, false)) === true;

      state.canStartViewAs = currentUserCanStartViewAs();
      refreshCanStartViewAs();
      bindCanStartViewAsAccessListener();
      state.viewAsFeatureEnabled = await proxy.$getFlag(
        LD_FLAG_IDEA_MDP_503_VIEW_AS,
        false
      );
      setViewAsFeatureFlag(state.viewAsFeatureEnabled);

      // ER-10611: multi-field search dropdown on the employee list (kill switch, default on).
      state.config.showSearchPositionByMultipleFieldBox = await proxy.$getFlag(
        LD_FLAG_EMPLOYEE_MULTI_FIELD_SEARCH,
        true
      );

      if (!isProductReadinessEnabled()) {
        state.filters.values = stripPreparesEmployeeFilterValues(state.filters.values);
        employees.removeEmployeeFilter("prepares");
      }
    } catch (e) {
      console.error("Error in initializeFilters:", e);
      // Still resolve admin eligibility if the flag call fails.
      state.canStartViewAs = currentUserCanStartViewAs();
      refreshCanStartViewAs();
      bindCanStartViewAsAccessListener();
    }
  };

  instance = {
    state,
    hasSelectedEmployees,
    currentEmployee,
    openAssignmentWizard,
    onAssignmentDateChange,
    openAssignmentPosition,
    openPreparePositionDialog,
    openPreparePosition,
    closeAssignmentWizard,
    closePreparePosition,
    closeRelieveAssignment,
    updateFilter: () => {},
    clearFilters: () => {},
    setLoading: () => {},
    getEmployeeCardProps,
    getEmployeeAssigmentTagProps,
    relievePrimaryPositionFromEmployee,
    getStateBadgeProps,
    handleEmployeeDropdownOperation,
    handleEmployeeAvatarClick,
    confirmViewAsAfterStatusWarning,
    handleEmployeeOperation,
    checkUserEmail,
    applyFilters,
    getFilterDisplayLabel,
    getFilterDisplayLabels,
    getFilterValueFromLabel,
    syncEmployeeApiFiltersFromState,
    setDepartmentFilter,
    reloadEmployeesPreservingFilters,
    removeFilters,
    giveAccessToEmployee,
    onAssignPositionToEmployee,
    onAssignPreparePositionToEmployee,
    clearSearch,
    getEmployeePrepareAssignmentCardProps,
    getSanitizedItems,
    paginatedAttachedRoles,
    openEmployeeChatterModal,
    closeEmployeeChatterModal,
    requestMoreChatter,
    onApplyDatePrepareFor,
    onApplyDatePrimaryAssignment,
    getEmployeeAccess,
    handleEmployeeSort,
    handleSearch,
    performSearch,
    handleRequest,
    handleMultipleFieldSearch,
    getListOfPrimaryPositions,
    loadMorePrimaryPositions,
    searchPrimaryPositions,
    getListOfPreparePositions,
    loadMorePreparePositions,
    searchPreparePositions,
    removeAllFiltersAndReload,
    activeIndicatorKey,
    selectIndicator,
    initializeFilters,
    loadFiltersOnReload,
    handleResendInvite,
    handleCancelInvite,
  };
  return instance;
}

export function resetEmployeesOrganizationTableViewState() {
  instance = null;
}

